import { COLOR_PALETTE } from 'constants/ui';

import { LabelBorderStyle } from './LabelCDS.constants';

// Status chips are keyed off a raw COLOR_PALETTE family + shade index (e.g. light bg shade 2,
// dark text shade 8) — none of which flips for dark mode, so a light-shade fill leaks near-white.
// In dark mode we mirror the shade index within the SAME family (lightest<->darkest), so a chip
// becomes a dark tint of its colour with a light-tint label, preserving the family hue and keeping
// the value in range for short families (e.g. ADDITIONAL). Light mode resolves the original index
// unchanged, so its appearance is byte-for-byte identical.
const getDarkShade = (variant, shade) => {
  const family = COLOR_PALETTE[variant];

  if (!family || shade == null) {
    return shade;
  }

  const mirrored = family.length - shade;

  return family[mirrored] != null ? mirrored : shade;
};

// Some families' darkest steps sit on top of the dark surfaces themselves, so the mirrored fill
// stops reading as a chip against palette.background.paper (#181F2A). The default fill (shade 9)
// measures GRAY 1.48:1, BRAND 1.25:1, BLUE_GRAY 1.30:1, CYAN 1.59:1 and PURPLE 1.72:1, while
// GREEN / RED / ORANGE keep ~1.9-2.2:1. Each cap is the darkest shade that reaches >= 1.8:1 on
// paper: GRAY[8] 2.15, BRAND[7] 2.30 (BRAND[8] is only 1.64), BLUE_GRAY[7] 1.92 (BLUE_GRAY[8] is
// only 1.66), CYAN[8] 2.72, PURPLE[8] 2.15. Border shades are never capped.
const DARK_FILL_MAX_SHADE = {
  GRAY: 8,
  BRAND: 7,
  BLUE_GRAY: 7,
  CYAN: 8,
  PURPLE: 8,
};

// A lighter fill costs text contrast, so where the mirrored text shade in use would drop below
// 4.5:1 on the capped fill, the text is lifted too: default text shade 3 is 3.93:1 on BRAND[7]
// and 4.25:1 on CYAN[8] (shade 2: 4.99 / 5.00); the soft preset's text shade 4 is 4.28:1 on
// BLUE_GRAY[7] and 4.09:1 on PURPLE[8] (shade 3: 6.13 / 5.51). The limit applies only while the
// fill sits at its cap (pulled down to it or mirrored exactly onto it), so a light-fill /
// dark-text chip keeps its mirrored text. GRAY text shades 3-5 already clear 5:1 on GRAY[8].
const DARK_TEXT_MAX_SHADE = {
  BRAND: 2,
  BLUE_GRAY: 3,
  CYAN: 2,
  PURPLE: 3,
};

const getDarkFillShade = (variant, shade) => {
  const mirrored = getDarkShade(variant, shade);
  const maxShade = DARK_FILL_MAX_SHADE[variant];

  return maxShade != null && mirrored > maxShade ? maxShade : mirrored;
};

const getDarkTextShade = (variant, shade, fillShade) => {
  const mirrored = getDarkShade(variant, shade);
  const maxTextShade = DARK_TEXT_MAX_SHADE[variant];
  const isFillAtCap = getDarkFillShade(variant, fillShade) === DARK_FILL_MAX_SHADE[variant];

  return maxTextShade != null && isFillAtCap && mirrored > maxTextShade ? maxTextShade : mirrored;
};

const resolveColor =
  (variant, shade, getDarkModeShade = getDarkShade) =>
  ({ palette }) =>
    COLOR_PALETTE[variant][palette.mode === 'dark' ? getDarkModeShade(variant, shade) : shade];

export const getLabelStyles = ({
  variant,
  colorMatch,
  withBorder,
  borderStyle = LabelBorderStyle.SOLID,
  shrinkable = false,
}) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  justifyContent: 'space-between',
  backgroundColor: resolveColor(variant, colorMatch.backgroundColor, getDarkFillShade),
  color: resolveColor(variant, colorMatch.color, (textVariant, textShade) =>
    getDarkTextShade(textVariant, textShade, colorMatch.backgroundColor),
  ),
  borderRadius: ({ shape }) => shape.radius.xl,
  // Opt-in: lets this chip shrink below its content's natural width when nested inside a
  // constrained flex row (e.g. a truncating value chip next to other meta text). Off by
  // default so the ~225 existing consumers keep the flex `min-width: auto` behavior; only
  // a consumer that truncates inside a squeeze (the document discriminator tag) opts in.
  ...(shrinkable && {
    minWidth: 0,
  }),
  ...(withBorder && {
    border: theme => `1px ${borderStyle} ${resolveColor(variant, colorMatch.borderColor)(theme)}`,
  }),
});
